import React, { useMemo, useCallback, useRef } from 'react';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { executeSophiaAction, selectThread, selectSelectedProducts } from '../thread/threadSlice';
import { NO_IMAGE_PLACEHOLDER, normaliseProductDetails } from '../../utils/product';
import DetailsModal from '../../components/DetailsModal';

interface ProductItem {
  sku: string;
  code: string;
  img: string;
}

interface SimilarityData {
  sku_1: string;
  sku_2: string;
  similarity: {
    Combined: number;
    [key: string]: number;
  }
}

interface SophiaSimilarityMatrixResponse {
  'global-page-data'?: {
    similarity_data: SimilarityData[];
    products_data: Record<string, unknown>;
  };
  [key: string]: unknown;
}

function heatColor(value: number): string {
  const v = Math.max(0, Math.min(1, value));
  if (v <= 0.70) {
    const t = v / 0.70;
    // Interpolate from #CE9A85 (orange) to #F3F3C8 (yellowish)
    const r = Math.round(206 + (243 - 206) * t);
    const g = Math.round(154 + (243 - 154) * t);
    const b = Math.round(133 + (200 - 133) * t);
    return `rgb(${r},${g},${b})`;
  }
  const t = (v - 0.70) / 0.30;
  // Interpolate from #F3F3C8 (yellowish) to #85CE88 (green)
  const r = Math.round(243 + (133 - 243) * t);
  const g = Math.round(243 + (206 - 243) * t);
  const b = Math.round(200 + (136 - 200) * t);
  return `rgb(${r},${g},${b})`;
}

// Global cache: stores loaded data per threadId to prevent duplicate loads
interface CachedData {
  products: ProductItem[];
  similarity: number[][];
}
const globalCache = new Map<string, CachedData>();
const loadingThreads = new Set<string>();

export function SimilarityMatrix() {
  const dispatch = useAppDispatch();
  const thread = useAppSelector(selectThread);
  const threadId = thread?.threadId;
  const selectedSkus = useAppSelector(selectSelectedProducts);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [similarity, setSimilarity] = useState<number[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedProductSku, setSelectedProductSku] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Calcola le dimensioni iniziali per far stare la matrice nella viewport
  const calculateInitialZoom = useCallback((numProducts: number) => {
    if (!containerRef.current || numProducts === 0) return 1;

    const containerWidth = containerRef.current.clientWidth - 48; // padding laterale
    const containerHeight = containerRef.current.clientHeight - 48; // solo padding, non controlli zoom

    // Dimensioni base della griglia
    const baseProductColumnWidth = 240; // Allineato con scaledDimensions
    const baseCellSize = 112;
    const baseGap = 3;

    // Calcolo larghezza totale: colonna prodotti + celle + gap (tenendo conto del gap raddoppiato)
    const actualGap = Math.max(6, baseGap * 2);
    const totalWidth = baseProductColumnWidth + (numProducts * baseCellSize) + (actualGap * (numProducts));

    // Calcolo altezza totale: righe prodotti + riga footer + gap (tenendo conto del gap raddoppiato)
    // Ogni riga ha l'altezza della cella (112px) + gap
    const totalHeight = (numProducts * baseCellSize) + baseCellSize + (actualGap * (numProducts + 1));

    // Calcola i rapporti di scaling
    const widthRatio = containerWidth / totalWidth;
    const heightRatio = containerHeight / totalHeight;

    // Usa il rapporto più piccolo per assicurarsi che tutto sia visibile
    const optimalZoom = Math.min(widthRatio, heightRatio, 1);

    // Assicurati che il zoom non sia troppo piccolo per essere leggibile
    return Math.max(optimalZoom, 0.15);
  }, []);

  useEffect(() => {
    if (!threadId) {
      return;
    }

    // Create cache key that includes selected products to invalidate when selection changes
    const selectedSkusKey = [...selectedSkus].sort().join(',');
    const cacheKey = `${threadId}:${selectedSkusKey}`;

    // Check if data is already cached for this thread + selection combination
    const cached = globalCache.get(cacheKey);
    if (cached) {
      setProducts(cached.products);
      setSimilarity(cached.similarity);
      if (isInitialLoad && cached.products.length > 0) {
        const initialZoom = calculateInitialZoom(cached.products.length);
        setZoomLevel(initialZoom);
        setIsInitialLoad(false);
      }
      return;
    }

    // Check if already loading for this combination
    if (loadingThreads.has(cacheKey)) {
      return;
    }

    async function load() {
      if (!threadId) return; // Type guard
      try {
        loadingThreads.add(cacheKey);
        setLoading(true);
        const payload = await dispatch(
            executeSophiaAction({
              actionName: 'global-get-page-data',
              params: { 'global-page-name': 'similarity-matrix' },
              refreshThread: false,
            }),
          ).unwrap();
        const response = payload.data as SophiaSimilarityMatrixResponse;
        const rawProductsData = response?.['global-page-data']?.products_data;
        const productsData = Array.isArray(rawProductsData) ? rawProductsData : [];
        const rawSimilarityData = response?.['global-page-data']?.similarity_data;
        const similarityData = Array.isArray(rawSimilarityData) ? rawSimilarityData : [];

        const products: ProductItem[] = productsData.map((x: any) => {
          const normalized = normaliseProductDetails(x);
          return {
            sku: normalized.sku || x.sku,
            code: normalized.code || normalized.sku || x.sku,
            img: normalized.images[0] ?? NO_IMAGE_PLACEHOLDER,
          };
        });

        const knownProductSkus = new Set(products.map(product => product.sku));
        selectedSkus.forEach(sku => {
          if (!knownProductSkus.has(sku)) {
            products.push({
              sku,
              code: sku,
              img: NO_IMAGE_PLACEHOLDER,
            });
          }
        });

        const similarity = products.map((x: ProductItem) =>
          products.map((y: ProductItem) =>
            similarityData.find((z: any) =>
              x.sku === z?.sku_1 && y.sku === z?.sku_2
            )?.similarity?.Combined ?? 0
          )
        );

        // Cache the data with the new key
        globalCache.set(cacheKey, { products, similarity });

        setProducts(products);
        setSimilarity(similarity);

        // Imposta lo zoom iniziale per far stare tutto nella viewport solo al primo caricamento
        if (isInitialLoad) {
          const initialZoom = calculateInitialZoom(products.length);
          setZoomLevel(initialZoom);
          setIsInitialLoad(false);
        }

        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
        loadingThreads.delete(cacheKey);
      }
    }
    void load();
  }, [threadId, selectedSkus, dispatch, calculateInitialZoom, isInitialLoad]);

  // Gestisce il resize della finestra per mantenere il fit quando necessario
  useEffect(() => {
    const handleResize = () => {
      // Solo se siamo in modalità "fit to page" (zoom relativamente basso)
      if (zoomLevel < 0.8 && products.length > 0) {
        const newZoom = calculateInitialZoom(products.length);
        setZoomLevel(newZoom);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [zoomLevel, products.length, calculateInitialZoom]);

  // Calcola le dimensioni scalate basate sul zoom
  const scaledDimensions = useMemo(() => {
    const baseProductColumnWidth = 240; // Aumentato per evitare sovrapposizioni
    const baseCellSize = 112;
    const baseGap = 3;
    const baseImageSize = 112;
    const baseFontSize = 16;
    const baseButtonFontSize = 12;

    const imageSize = Math.round(baseImageSize * zoomLevel);
    const gap = Math.round(baseGap * zoomLevel);
    const productColumnWidth = Math.round(baseProductColumnWidth * zoomLevel);

    return {
      productColumnWidth,
      cellSize: Math.round(baseCellSize * zoomLevel),
      gap,
      imageSize,
      fontSize: Math.max(8, Math.round(baseFontSize * zoomLevel)),
      buttonFontSize: Math.max(6, Math.round(baseButtonFontSize * zoomLevel)),
      buttonPaddingY: Math.max(1, Math.round(4 * zoomLevel)),
      buttonPaddingX: Math.max(2, Math.round(8 * zoomLevel)),
      cellFontSize: Math.max(8, Math.round(16 * zoomLevel)),
    };
  }, [zoomLevel]);

  const columnsStyle = useMemo(() =>
    `${scaledDimensions.productColumnWidth}px repeat(${products.length}, ${scaledDimensions.cellSize}px)`,
    [products.length, scaledDimensions]
  );

  // Funzioni di controllo zoom
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.15));
  }, []);



  const handleShowDetails = useCallback(
    (sku: string) => {
      setSelectedProductSku(sku);
      setDetailsModalOpen(true);
    },
    [],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedProductSku(null);
  }, []);

  return (
    <div ref={containerRef} className="grow w-full h-full pl-6 pt-6 relative">
      <div className="w-full h-full overflow-auto">
        {/* Controlli Zoom - Sovrapposti in basso a destra */}
        <div className="absolute bottom-6 right-6 flex items-center gap-2 z-10 pointer-events-auto">
          <button
            type="button"
            onClick={handleZoomOut}
            className="px-3 py-2 text-sm font-semibold bg-black text-white border border-black hover:bg-gray-800"
            disabled={zoomLevel <= 0.15}
          >
            -
          </button>
          <span className="text-sm font-medium min-w-[60px] text-center px-2 py-2 bg-black text-white border border-black">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            type="button"
            onClick={handleZoomIn}
            className="px-3 py-2 text-sm font-semibold bg-black text-white border border-black hover:bg-gray-800"
            disabled={zoomLevel >= 3}
          >
            +
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-full w-full" style={{ marginTop: '30px' }}>
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin-in rounded-full h-16 w-16 border-b-4 border-black"></div>
              <p className="text-gray-600 font-medium">Caricamento matrice...</p>
            </div>
          </div>
        ) : error ? (
          <p className="p-4 text-sm text-red-500">{error}</p>
        ) : (
          <div
            className="grid"
            style={{
              gridTemplateColumns: columnsStyle,
              gap: `${Math.max(6, scaledDimensions.gap * 2)}px`
            }}
          >
        {products.map((product, rowIndex) => (
          <React.Fragment key={`row-${product.sku}-${rowIndex}`}>
            <div
              className="flex flex-row justify-between items-center overflow-hidden"
              style={{
                width: `${scaledDimensions.productColumnWidth}px`,
                height: `${scaledDimensions.cellSize}px`,
                gap: `${Math.max(8, scaledDimensions.gap * 3)}px`
              }}
            >
              <div className="flex flex-col items-end justify-center flex-shrink-0 gap-y-1" style={{
                width: `${scaledDimensions.productColumnWidth - scaledDimensions.imageSize - Math.max(8, scaledDimensions.gap * 3)}px`,
                maxWidth: `${scaledDimensions.productColumnWidth - scaledDimensions.imageSize - Math.max(8, scaledDimensions.gap * 3)}px`
              }}>
                <span
                  className="text-right leading-tight overflow-hidden text-ellipsis"
                  style={{
                    fontSize: `${scaledDimensions.fontSize}px`,
                    lineHeight: `${scaledDimensions.fontSize * 1.1}px`,
                    marginBottom: `${Math.max(2, 4 * zoomLevel)}px`,
                    wordBreak: 'break-word',
                    hyphens: 'auto'
                  }}
                >
                  {product.code}
                </span>
                <button
                  type="button"
                  onClick={() => handleShowDetails(product.sku)}
                  className="text-xs font-semibold bg-white text-black border-2 border-black flex-shrink-0"
                  style={{
                    fontSize: `${scaledDimensions.buttonFontSize}px`,
                    padding: `${scaledDimensions.buttonPaddingY}px ${scaledDimensions.buttonPaddingX}px`,
                    lineHeight: `${scaledDimensions.buttonFontSize}px`
                  }}
                >
                  DETTAGLI
                </button>
              </div>
              <img
                src={product.img}
                alt={product.code}
                className="object-cover flex-shrink-0"
                style={{
                  width: `${scaledDimensions.imageSize}px`,
                  height: `${scaledDimensions.imageSize}px`,
                  borderRadius: `${Math.max(2, 8 * zoomLevel)}px`
                }}
              />
            </div>
            {products.map((_, columnIndex) => {
              const isDiagonal = rowIndex === columnIndex;

              return (
                <div
                  key={`cell-${rowIndex}-${columnIndex}`}
                  className="flex items-center justify-center font-semibold"
                  style={{
                    background: isDiagonal ? '#f3f3f3' : heatColor(similarity[rowIndex][columnIndex]),
                    width: `${scaledDimensions.cellSize}px`,
                    height: `${scaledDimensions.cellSize}px`,
                    fontSize: `${scaledDimensions.cellFontSize}px`,
                    lineHeight: `${scaledDimensions.cellFontSize}px`,
                    opacity: isDiagonal ? 0.3 : 1
                  }}
                >
                  {isDiagonal ? '-' : (similarity[rowIndex][columnIndex]?.toFixed(2) ?? '0.00')}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        <div style={{ padding: `${scaledDimensions.gap}px` }} />
        {products.map(product => (
          <img
            key={`footer-${product.sku}`}
            src={product.img}
            alt={product.code}
            className="object-cover"
            style={{
              width: `${scaledDimensions.imageSize}px`,
              height: `${scaledDimensions.imageSize}px`,
              borderRadius: `${Math.max(2, 8 * zoomLevel)}px`
            }}
          />
        ))}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <DetailsModal
        sku={selectedProductSku}
        isOpen={detailsModalOpen}
        onClose={handleCloseDetailsModal}
      />
    </div>
  );
}

export default SimilarityMatrix;
